import { useState } from "react"
import styled from "styled-components"
import { UseFormRegister, UseFormWatch } from "react-hook-form"
import { ModalTextarea } from "~styled/textarea"

interface AISuggestionNoteProps {
  register: UseFormRegister<any>
  watch: UseFormWatch<any>
  disabled?: boolean
}

// Sits next to the analyzer version badge in the header — a note about the
// operator's review, not about the document, so it isn't tied to any one
// suggestion box's color/state. Opens on hover/focus, closes on leave/blur (no
// click); the dot marks that a note was already typed so it isn't lost from
// view while closed. Rendered above the header's own FormProvider, so it takes
// register/watch as props instead of reading them from form context.
const AISuggestionNote = ({
  register,
  watch,
  disabled = false,
}: AISuggestionNoteProps) => {
  const [expanded, setExpanded] = useState(false)
  const noteValue: string = watch("notes") || ""
  const hasValue = Boolean(noteValue)
  const maxLength = 500

  return (
    <Wrapper
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={() => setExpanded(false)}
    >
      <Toggle
        tabIndex={0}
        aria-expanded={expanded}
        title="Hover to add a note about this review"
      >
        <Icon aria-hidden>📝</Icon>
        Review note
        {hasValue && !expanded && <Dot aria-hidden />}
      </Toggle>
      {expanded && (
        <Panel>
          <Label>Review note</Label>
          <ModalTextarea
            {...register("notes")}
            placeholder="Add context for this review (optional)"
            style={{ height: 80 }}
            maxLength={maxLength}
            disabled={disabled}
          />
          <CharCount>
            {noteValue.length}/{maxLength}
          </CharCount>
        </Panel>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: relative;
`

const Toggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 4px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  cursor: default;

  &:hover,
  &:focus-visible {
    color: #374151;
    outline: none;
  }
`

const Icon = styled.span`
  font-size: 12px;
  line-height: 1;
`

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #6b7280;
`

// Absolutely positioned so opening it doesn't reflow the header row.
const Panel = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  width: 320px;
  padding: 14px 10px 10px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
`

const Label = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`

const CharCount = styled.div`
  text-align: right;
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
`

export default AISuggestionNote
